import type { ReactNode } from "react";
import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { formatDateTime, formatRelative } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * GitHub connection status (TASK-807, SPEC §56).
 *
 * Read-only except for `Sync`, which is the one operation an operator needs
 * when D1 and the repository disagree. Making the CMS able to *change* GitHub
 * configuration would mean it held credentials it does not need.
 */
export function GitHubSettings() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.getGitHubStatus(siteId);
  }, [siteId]);

  async function runSync(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    setSyncing(true);
    setMessage(null);
    setError(null);

    try {
      const result = await client.sync(siteId);

      setMessage(
        t("github.synced", {
          rev: result.revision.slice(0, 7),
          created: result.entriesCreated,
          updated: result.entriesUpdated,
          localizations: result.localizations,
        }),
      );
      status.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("github.syncFailed"));
    } finally {
      setSyncing(false);
    }
  }

  if (siteLoading || status.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (status.error !== null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-red-600">{status.error}</p>
        </CardContent>
      </Card>
    );
  }

  const github = status.data?.github;

  return (
    <>
      <PageHeader title={t("github.title")} description={t("github.desc")}>
        <Button size="sm" loading={syncing} onClick={() => void runSync()}>
          {t("github.syncNow")}
        </Button>
      </PageHeader>

      {message === null ? null : (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {message}
        </div>
      )}
      {error === null ? null : (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("github.connection")}</CardTitle>
          <CardDescription>
            {github?.owner ?? "—"}/{github?.repo ?? "—"}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <Row label={t("common.installation")}>
            {github?.installationId ?? site?.githubInstallationId ?? "—"}
          </Row>
          <Row label={t("common.webhook")}>
            <Badge variant={github?.webhookConfigured === true ? "success" : "warning"}>
              {github?.webhookConfigured === true
                ? t("common.configured")
                : t("common.notConfigured")}
            </Badge>
          </Row>
          <Row label={t("common.lastSync")}>
            {github?.lastSyncAt == null ? (
              t("common.never")
            ) : (
              <>
                {formatDateTime(github.lastSyncAt)}{" "}
                <span className="text-neutral-400">
                  ({formatRelative(github.lastSyncAt, Date.now())})
                </span>
              </>
            )}
          </Row>
          <Row label={t("common.lastActivity")}>
            {github?.lastActivityAt == null
              ? t("common.never")
              : formatRelative(github.lastActivityAt, Date.now())}
          </Row>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>{t("github.index")}</CardTitle>
          <CardDescription>{t("github.indexDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("github.thKind")}</TableHead>
                <TableHead>{t("github.thState")}</TableHead>
                <TableHead className="text-right">{t("github.thCount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(status.data?.content.entries ?? {}).map(([kind, count]) => (
                <TableRow key={`entry:${kind}`}>
                  <TableCell>{t("github.kindEntry")}</TableCell>
                  <TableCell>{kind}</TableCell>
                  <TableCell className="text-right">{count}</TableCell>
                </TableRow>
              ))}
              {Object.entries(status.data?.content.localizations ?? {}).map(([state, count]) => (
                <TableRow key={`loc:${state}`}>
                  <TableCell>{t("github.kindLocalization")}</TableCell>
                  <TableCell>{state}</TableCell>
                  <TableCell className="text-right">{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-800">{children}</span>
    </div>
  );
}
